import React, { useState } from 'react';
import {
    Stack,
    Label,
    Dropdown,
    IDropdownOption,
    Link,
    MessageBar,
    MessageBarType,
    Checkbox,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
} from '@fluentui/react';
import type { ContainerTag, FileInfo } from '../../../api/admin/models_admin';
import { getDownloadURL } from '../../../api';
import { CellContent, displayFileName, formatDateTime, formatFileSize, naturalCollator, filterByKeyword, ListFilterBox } from '../components';

interface UpdateTagsFilePanelProps {
    disabled: boolean
    // 編集中のサブカテゴリ定義。未保存のサブカテゴリもここに含まれる
    containerTags: ContainerTag[]
    files: FileInfo[]
    // 編集中のタグ付与状態。サーバへの反映は親の「編集を完了して検索へ反映」で行う
    fileTagKeys: Record<number, string[]>
    setFileTagKeys: React.Dispatch<React.SetStateAction<Record<number, string[]>>>
    // 選択中のサブカテゴリと絞り込み文字列はタブを切り替えても保持したいため親が持つ
    editingTagKey: string | undefined
    setEditingTagKey: (value: string | undefined) => void
    fileNameFilter: string
    setFileNameFilter: (value: string) => void
}

export const UpdateTagsFilePanel = (props: UpdateTagsFilePanelProps) => {
    const { disabled, containerTags, files, fileTagKeys, setFileTagKeys,
        editingTagKey, setEditingTagKey, fileNameFilter, setFileNameFilter } = props

    // 並び順は表示上の状態なのでパネル内に持つ。初期状態はファイル名の昇順
    const [sortedColumnKey, setSortedColumnKey] = useState<string>('filename');
    const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);

    // ドロップダウンの選択肢は編集中のコンテナタグ (キー -> 値)
    const tagOptions: IDropdownOption[] = containerTags.map(t => ({ key: t.key, text: t.value }));

    // 編集中にサブカテゴリが削除された場合に、存在しないキーを選択したままにしない
    const activeTagKey = tagOptions.some(o => o.key === editingTagKey) ? editingTagKey : undefined;

    const hasTag = (fileSeq: number, tagKey: string): boolean =>
        (fileTagKeys[fileSeq] ?? []).includes(tagKey);

    // ファイルに付与されている全サブカテゴリ名。選択中以外の付与状況も一覧で確認できるようにする
    const tagValueByKey = new Map(containerTags.map(t => [t.key, t.value]));
    const assignedTagLabel = (fileSeq: number): string =>
        (fileTagKeys[fileSeq] ?? [])
            .map(k => tagValueByKey.get(k))
            .filter((v): v is string => Boolean(v))
            .join('、');

    // 絞り込み → 並び替えの順に適用する
    const filteredFiles = filterByKeyword(files, fileNameFilter, f => displayFileName(f));

    const sortedFiles = [...filteredFiles].sort((a, b) => {
        let compareResult = 0;
        switch (sortedColumnKey) {
            case 'checked':
                // 未選択(false)→選択済み(true)を昇順とする
                compareResult = Number(activeTagKey ? hasTag(a.seq, activeTagKey) : false)
                    - Number(activeTagKey ? hasTag(b.seq, activeTagKey) : false);
                break;
            case 'tags':
                compareResult = naturalCollator.compare(assignedTagLabel(a.seq), assignedTagLabel(b.seq));
                break;
            case 'size':
                compareResult = a.size - b.size;
                break;
            case 'type':
                compareResult = naturalCollator.compare(a.type || '', b.type || '');
                break;
            case 'timestamp':
                compareResult = new Date(a.timestamp ?? "").getTime() - new Date(b.timestamp ?? "").getTime();
                break;
            case 'filename':
            default:
                compareResult = naturalCollator.compare(displayFileName(a), displayFileName(b));
                break;
        }
        // 同値の場合はファイル名で決着させ、並び順を確定的にする
        if (compareResult === 0 && sortedColumnKey !== 'filename') {
            return naturalCollator.compare(displayFileName(a), displayFileName(b));
        }
        return isSortedDescending ? -compareResult : compareResult;
    });

    const onColumnClick = (_ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
        if (!column) return;
        setIsSortedDescending(sortedColumnKey === column.key ? !isSortedDescending : false);
        setSortedColumnKey(column.key);
    };

    // 指定ファイル群に対して、選択中のサブカテゴリを一括で付与/解除する
    const setTagOnFiles = (fileSeqs: number[], checked: boolean) => {
        if (!activeTagKey) return;
        setFileTagKeys(prev => {
            const next = { ...prev };
            for (const fileSeq of fileSeqs) {
                const current = next[fileSeq] ?? [];
                if (checked) {
                    if (!current.includes(activeTagKey)) next[fileSeq] = [...current, activeTagKey];
                } else {
                    next[fileSeq] = current.filter(k => k !== activeTagKey);
                }
            }
            return next;
        });
    };

    const checkedFilteredCount = activeTagKey
        ? filteredFiles.filter(f => hasTag(f.seq, activeTagKey)).length
        : 0;
    const allFilteredChecked = filteredFiles.length > 0 && checkedFilteredCount === filteredFiles.length;
    const someFilteredChecked = checkedFilteredCount > 0 && !allFilteredChecked;

    // 全列に共通のソート用プロパティ
    const sortProps = (key: string) => ({
        isSorted: sortedColumnKey === key,
        isSortedDescending: isSortedDescending,
        onColumnClick: onColumnClick,
    });

    const columns: IColumn[] = [
        {
            key: 'checked',
            name: '選択',
            minWidth: 40,
            maxWidth: 60,
            ...sortProps('checked'),
            onRender: (file: FileInfo) => (
                <CellContent>
                    <Checkbox
                        checked={activeTagKey ? hasTag(file.seq, activeTagKey) : false}
                        ariaLabel={displayFileName(file)}
                        disabled={disabled}
                        onChange={(_, checked) => setTagOnFiles([file.seq], Boolean(checked))}
                    />
                </CellContent>
            )
        },
        {
            key: 'filename',
            name: 'ファイル名',
            fieldName: 'filename',
            minWidth: 200,
            // 長い名前が多い列なので上限を広く取る。justifiedレイアウトでは前の列から順に
            // 上限まで広がり、余った幅は最終列が吸うため、ここを広げると配分が改善する
            maxWidth: 500,
            isResizable: true,
            ...sortProps('filename'),
            onRender: (file: FileInfo) => (
                <CellContent tooltip={displayFileName(file)}>
                    <Link
                        href={getDownloadURL(file.container_seq, `${file.filename}` + (file.type.length !== 0 ? `.${file.type}` : ""))}
                        target="_blank"
                    >
                        {file.filename}
                    </Link>
                </CellContent>
            )
        },
        {
            key: 'tags',
            name: '付与済みサブカテゴリ',
            minWidth: 150,
            maxWidth: 400,
            isResizable: true,
            ...sortProps('tags'),
            onRender: (file: FileInfo) => (
                <CellContent>{assignedTagLabel(file.seq) || '-'}</CellContent>
            )
        },
        {
            key: 'size',
            name: 'サイズ',
            fieldName: 'size',
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            ...sortProps('size'),
            onRender: (file: FileInfo) => (
                <CellContent>{formatFileSize(file.size)}</CellContent>
            )
        },
        {
            key: 'type',
            name: '種別',
            fieldName: 'type',
            minWidth: 60,
            maxWidth: 100,
            isResizable: true,
            ...sortProps('type'),
            onRender: (file: FileInfo) => (
                <CellContent>{file.type}</CellContent>
            )
        },
        {
            key: 'timestamp',
            name: 'アップロード日時',
            fieldName: 'timestamp',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            ...sortProps('timestamp'),
            onRender: (file: FileInfo) => (
                <CellContent>{formatDateTime(file.timestamp)}</CellContent>
            )
        },
    ];

    return (
        <Stack tokens={{ childrenGap: 12, padding: '20px 0' }}>
            {tagOptions.length === 0 && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    先に「サブカテゴリ作成」タブでサブカテゴリを登録してください
                </MessageBar>
            )}

            {files.length === 0 ? (
                <Label styles={{ root: { color: '#666' } }}>ファイルがありません</Label>
            ) : (
                <>
                    <Dropdown
                        label="設定するサブカテゴリ"
                        options={tagOptions}
                        selectedKey={activeTagKey ?? null}
                        onChange={(_, option) => {
                            setEditingTagKey(option ? String(option.key) : undefined);
                        }}
                        placeholder="サブカテゴリを選択"
                        disabled={tagOptions.length === 0 || disabled}
                        styles={{ root: { maxWidth: 400 } }}
                    />

                    {!activeTagKey ? (
                        <Label styles={{ root: { color: '#666' } }}>
                            サブカテゴリを選択すると、ファイル一覧が表示されます
                        </Label>
                    ) : (
                        <>
                            <ListFilterBox
                                value={fileNameFilter}
                                onChange={setFileNameFilter}
                                placeholder="ファイル名で絞り込み"
                            />

                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                                <Checkbox
                                    label={`表示中の${filteredFiles.length}件をすべて選択`}
                                    checked={allFilteredChecked}
                                    indeterminate={someFilteredChecked}
                                    disabled={filteredFiles.length === 0 || disabled}
                                    onChange={() => setTagOnFiles(filteredFiles.map(f => f.seq), !allFilteredChecked)}
                                    styles={{ root: { fontWeight: 600 } }}
                                />
                                <Label styles={{ root: { color: '#666', fontWeight: 400 } }}>
                                    （{checkedFilteredCount} / {filteredFiles.length} 件選択中）
                                </Label>
                            </Stack>

                            {filteredFiles.length === 0 ? (
                                <Label styles={{ root: { color: '#666' } }}>
                                    絞り込み条件に一致するファイルがありません
                                </Label>
                            ) : (
                                // ファイル数が多い場合はこの領域だけをスクロールさせ、
                                // 絞り込みを画面内に留める
                                <Stack
                                    styles={{
                                        root: {
                                            maxHeight: 400,
                                            overflowY: 'auto',
                                            border: '1px solid #edebe9',
                                            borderRadius: 2,
                                        },
                                    }}
                                >
                                    <DetailsList
                                        items={sortedFiles}
                                        columns={columns}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        selectionMode={SelectionMode.none}
                                        isHeaderVisible={true}
                                        styles={{
                                            // スクロールしても列ヘッダー（＝ソートの起点）を上端に残す
                                            root: { overflow: 'visible' },
                                            headerWrapper: {
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1,
                                                backgroundColor: '#fff',
                                            },
                                        }}
                                    />
                                </Stack>
                            )}
                        </>
                    )}
                </>
            )}
        </Stack>
    );
};
